/* global React */
// Main thread optimization utilities
export class MainThreadOptimizer {
  constructor() {
    this.taskQueue = [];
    this.isProcessing = false;
    this.frameId = null;
    this.idleCallback = null;
  }

  // Schedule tasks to run during idle time
  scheduleIdleTask(task, priority = "normal") {
    this.taskQueue.push({ task, priority, timestamp: performance.now() });
    this.processQueue();
  }

  // Process task queue during idle periods
  processQueue() {
    if (this.isProcessing) return;

    this.isProcessing = true;

    const processTask = (deadline) => {
      // Sort tasks by priority (high -> normal -> low)
      this.taskQueue.sort((a, b) => {
        const priorityOrder = { high: 3, normal: 2, low: 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      });

      while (this.taskQueue.length > 0 && deadline.timeRemaining() > 0) {
        const { task } = this.taskQueue.shift();
        try {
          task();
        } catch (error) {
          console.warn("Idle task failed:", error);
        }
      }

      this.isProcessing = false;

      // Schedule next batch if tasks remain
      if (this.taskQueue.length > 0) {
        this.scheduleNextBatch();
      }
    };

    this.scheduleNextBatch(processTask);
  }

  // Schedule next batch of tasks
  scheduleNextBatch(callback = null) {
    if ("requestIdleCallback" in window) {
      this.idleCallback = requestIdleCallback(
        callback || (() => this.processQueue()),
        {
          timeout: 1000, // Fallback timeout
        }
      );
    } else {
      // Fallback for browsers without requestIdleCallback (e.g. older mobile Safari)
      // Provide a mock deadline that gives ~10ms of budget per frame
      this.frameId = requestAnimationFrame(() => {
        const fallbackCallback = callback || (() => this.processQueue());
        const start = performance.now();
        const mockDeadline = {
          timeRemaining: () => Math.max(0, 10 - (performance.now() - start)),
          didTimeout: false,
        };
        setTimeout(() => fallbackCallback(mockDeadline), 0);
      });
    }
  }

  // Break up large tasks into smaller chunks
  breakUpTask(task, chunkSize = 5) {
    return new Promise((resolve) => {
      let index = 0;
      const totalItems = task.length || 100;

      const processChunk = () => {
        const startTime = performance.now();

        while (
          index < totalItems &&
          performance.now() - startTime < chunkSize
        ) {
          if (typeof task === "function") {
            task(index);
          } else if (Array.isArray(task)) {
            task[index]();
          }
          index++;
        }

        if (index < totalItems) {
          // Schedule next chunk
          this.scheduleIdleTask(() => processChunk(), "normal");
        } else {
          resolve();
        }
      };

      processChunk();
    });
  }

  // Optimize component rendering by deferring non-critical updates
  deferComponentUpdate(component, updateFn, priority = "normal") {
    this.scheduleIdleTask(() => {
      if (component && component.current) {
        updateFn();
      }
    }, priority);
  }

  // Optimize image loading to reduce main thread blocking
  optimizeImageLoading() {
    const images = document.querySelectorAll("img[data-src]");

    // Use Intersection Observer for lazy loading
    const imageObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;

            // Load image in idle time
            this.scheduleIdleTask(() => {
              img.src = img.dataset.src;
              img.removeAttribute("data-src");
              imageObserver.unobserve(img);
            }, "high");
          }
        });
      },
      {
        rootMargin: "50px 0px", // Start loading 50px before image is visible
        threshold: 0.1,
      }
    );

    images.forEach((img) => imageObserver.observe(img));
  }

  // Optimize script loading to prevent blocking
  optimizeScriptLoading() {
    const scripts = document.querySelectorAll("script[data-defer]");

    scripts.forEach((script, index) => {
      this.scheduleIdleTask(
        () => {
          const newScript = document.createElement("script");
          newScript.src = script.src;
          newScript.async = true;
          newScript.defer = true;

          // Add error handling
          newScript.onerror = () => {
            console.warn(`Failed to load script: ${script.src}`);
          };

          document.head.appendChild(newScript);
        },
        index < 2 ? "high" : "normal"
      ); // Prioritize first 2 scripts
    });
  }

  // Optimize CSS loading
  optimizeCSSLoading() {
    const stylesheets = document.querySelectorAll("link[data-defer]");

    stylesheets.forEach((link, index) => {
      this.scheduleIdleTask(
        () => {
          const newLink = document.createElement("link");
          newLink.rel = "stylesheet";
          newLink.href = link.dataset.href;
          newLink.media = "print"; // Load as print to prevent render blocking

          newLink.onload = () => {
            newLink.media = "all"; // Switch to all media after load
          };

          document.head.appendChild(newLink);
        },
        index < 1 ? "high" : "normal"
      ); // Prioritize first stylesheet
    });
  }

  // Optimize font loading
  optimizeFontLoading() {
    // Use font-display: swap for better performance (using Google Fonts instead of local files)
    const style = document.createElement("style");
    style.textContent = `
      @font-face {
        font-family: 'Space Grotesk';
        font-display: swap;
        src: url('https://fonts.gstatic.com/s/spacegrotesk/v15/V8mQoQDjQSkFtoMM3T6r8E7mF71Q-gOoraIAEj7oUXskPMBBSSJLm2E.woff2') format('woff2');
      }
    `;
    document.head.appendChild(style);

    // Preload critical fonts
    const fontLink = document.createElement("link");
    fontLink.rel = "preload";
    fontLink.href =
      "https://fonts.gstatic.com/s/spacegrotesk/v15/V8mQoQDjQSkFtoMM3T6r8E7mF71Q-gOoraIAEj7oUXskPMBBSSJLm2E.woff2";
    fontLink.as = "font";
    fontLink.type = "font/woff2";
    fontLink.crossOrigin = "anonymous";
    document.head.appendChild(fontLink);
  }

  // Monitor and optimize long tasks
  monitorLongTasks() {
    if ("PerformanceObserver" in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();

        entries.forEach((entry) => {
          if (entry.duration > 50) {
            console.warn(
              `Long task detected: ${entry.name} (${entry.duration}ms)`
            );

            // If we detect too many long tasks, reduce animations
            if (entry.duration > 100) {
              document.documentElement.style.setProperty(
                "--animation-duration",
                "0.1s"
              );
              document.documentElement.classList.add("reduce-motion");
            }
          }
        });
      });

      try {
        observer.observe({ entryTypes: ["longtask"] });
      } catch (e) {
        console.log("Long task monitoring not supported");
      }
    }
  }

  // Optimize React rendering
  optimizeReactRendering() {
    // Add performance marks for React components
    const originalRender = React.Component.prototype.render;

    React.Component.prototype.render = function () {
      const componentName = this.constructor.name;
      performance.mark(`${componentName}-render-start`);

      const result = originalRender.call(this);

      performance.mark(`${componentName}-render-end`);
      performance.measure(
        `${componentName}-render`,
        `${componentName}-render-start`,
        `${componentName}-render-end`
      );

      return result;
    };
  }

  // Clean up resources
  cleanup() {
    if (this.idleCallback) {
      cancelIdleCallback(this.idleCallback);
    }
    if (this.frameId) {
      cancelAnimationFrame(this.frameId);
    }
    this.taskQueue = [];
    this.isProcessing = false;
  }

  // Get performance metrics
  getPerformanceMetrics() {
    const navigation = performance.getEntriesByType("navigation")[0];
    const paint = performance.getEntriesByType("paint");

    return {
      domContentLoaded:
        navigation?.domContentLoadedEventEnd -
        navigation?.domContentLoadedEventStart,
      firstContentfulPaint: paint.find(
        (p) => p.name === "first-contentful-paint"
      )?.startTime,
      totalBlockingTime: this.calculateTotalBlockingTime(),
      taskQueueLength: this.taskQueue.length,
    };
  }

  // Calculate Total Blocking Time
  calculateTotalBlockingTime() {
    const longTasks = performance.getEntriesByType("longtask");
    let totalBlockingTime = 0;

    longTasks.forEach((task) => {
      if (task.duration > 50) {
        totalBlockingTime += task.duration - 50;
      }
    });

    return totalBlockingTime;
  }
}

// Singleton instance
export const mainThreadOptimizer = new MainThreadOptimizer();

// Auto-initialize optimizations
if (typeof window !== "undefined") {
  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      mainThreadOptimizer.optimizeImageLoading();
      mainThreadOptimizer.optimizeScriptLoading();
      mainThreadOptimizer.optimizeCSSLoading();
      mainThreadOptimizer.optimizeFontLoading();
      mainThreadOptimizer.monitorLongTasks();
    });
  } else {
    mainThreadOptimizer.optimizeImageLoading();
    mainThreadOptimizer.optimizeScriptLoading();
    mainThreadOptimizer.optimizeCSSLoading();
    mainThreadOptimizer.optimizeFontLoading();
    mainThreadOptimizer.monitorLongTasks();
  }
}

// Export utility functions
export const scheduleIdleTask = (task, priority = "normal") => {
  mainThreadOptimizer.scheduleIdleTask(task, priority);
};

export const breakUpLargeTask = (task, chunkSize = 5) => {
  return mainThreadOptimizer.breakUpTask(task, chunkSize);
};

export const deferComponentUpdate = (
  component,
  updateFn,
  priority = "normal"
) => {
  mainThreadOptimizer.deferComponentUpdate(component, updateFn, priority);
};

export const optimizeMainThread = () => {
  mainThreadOptimizer.optimizeImageLoading();
  mainThreadOptimizer.optimizeScriptLoading();
  mainThreadOptimizer.optimizeCSSLoading();
  mainThreadOptimizer.optimizeFontLoading();
  mainThreadOptimizer.monitorLongTasks();
};
